import { ReactNode, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import {
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { Stack } from 'src/core/ui';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { PurchaseOrderTable } from 'src/tables/PurchaseTable';
import { PurchaseOrderTableProps } from 'src/tables/PurchaseTable/PurchaseTable.types';
import {
  PurchaseOrderTicketGroup,
  PurchaseViewMode,
} from 'src/WebApiController';

type PurchaseFlattenedViewProps = {
  before?: ReactNode;
  topListItemBefore?: ReactNode;
} & Pick<
  PurchaseOrderTableProps,
  | 'alwaysShowCheckbox'
  | 'showSelectAllOnHeader'
  | 'enableColumnFilters'
  | 'ignoreMaxCount'
>;

export const PurchaseFlattenedView = ({
  before,
  topListItemBefore,
  ...rest
}: PurchaseFlattenedViewProps) => {
  const {
    allPurchasesQuery,
    allPurchasesFiltered,
    purchaseCount,
    allPurchasesSummary,
  } = usePurchaseDataContext();
  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();

  const hasTableTotalsFeature = useUserHasTableTotalsFeature();

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (!isEqual(allGroupsIds, [NO_GROUP_ID])) {
      setAllGroupsIds([NO_GROUP_ID]);
    }
  }, [allGroupsIds, setAllGroupsIds]);

  const purchases = useMemo(
    () =>
      (allPurchasesFiltered ?? []).map((p) => p as PurchaseOrderTicketGroup),
    [allPurchasesFiltered]
  );

  const entitiesAggregated = useMemo(
    () =>
      hasTableTotalsFeature && allPurchasesSummary != null
        ? (allPurchasesSummary as PurchaseOrderTicketGroup)
        : undefined,
    [hasTableTotalsFeature, allPurchasesSummary]
  );

  return (
    <Stack direction="column" height="full" width="full">
      {before}
      {topListItemBefore}
      <PurchaseOrderTable
        {...rest}
        groupId={NO_GROUP_ID}
        useVirtuoso
        disablePagination
        entityCount={purchaseCount}
        entities={purchases}
        entitiesSummary={entitiesAggregated}
        getDataFail={Boolean(allPurchasesQuery.error)}
        isItemsLoading={allPurchasesQuery.isLoading}
        viewMode={PurchaseViewMode.FlattenedView}
      />
    </Stack>
  );
};
